import {useCallback, useEffect, useState} from 'react';
import NextLink from 'next/link';

import {CustomerSubscriptionInfoBoxesFragment, useUpdateSubscriptionMutation} from '#apollo/schema';
import {Text} from '#component/base';

import {profileUpcomingOrderQuery} from '../../../../SubscriptionTab/useSubscriptionTab';
import {
  infoBox,
  infoBoxAppliedCouponsInnerText,
  infoBoxAppliedCouponsInnerWrapper,
  infoBoxAppliedCouponsWrapper,
} from '../../InfoBoxes.css';
import {
  innerContentEditText,
  innerContentEditWrapper,
  innerContentHeading,
  innerContentHeadingText,
} from '../../InfoBoxes.css';
import {profileInfoBoxesQuery} from '../../useInfoBoxes';
import AppliedCodeBox from './AppliedCodeBox';
import {getSubscriptionDiscountCodes} from './CouponBox.util';
import FormCoupon from './FormCoupon';

type CouponBoxProps = {
  subscription: CustomerSubscriptionInfoBoxesFragment;
};

const CouponBox = ({subscription}: CouponBoxProps) => {
  const [errorRemove, setErrorRemove] = useState<string | null>(null);
  const [discountCodesSub, setDiscountCodesSub] = useState<string[]>([]);
  const [updateSubscription] = useUpdateSubscriptionMutation({
    refetchQueries: [{query: profileInfoBoxesQuery}, {query: profileUpcomingOrderQuery}],
  });

  const hasMembership = subscription.hasMembership;

  useEffect(() => {
    if (subscription) {
      setDiscountCodesSub(getSubscriptionDiscountCodes(subscription));
    }
  }, [subscription]);

  const handleAddCoupon = useCallback(
    async (coupon: string) => {
      const subs = await updateSubscription({
        variables: {subscriptionChanges: {addDiscountCodes: [coupon]}},
      });
      return subs;
    },
    [updateSubscription],
  );

  const handleRemoveCoupon = useCallback(
    async (coupon: string) => {
      try {
        updateSubscription({
          variables: {subscriptionChanges: {removeDiscountCodes: [coupon]}},
        });
        setDiscountCodesSub(discountCodesSub.filter(code => code !== coupon));
      } catch (error) {
        setErrorRemove('Error removing coupon');
        setTimeout(() => {
          setErrorRemove(null);
        }, 3000);
      }
    },
    [discountCodesSub, updateSubscription],
  );

  return (
    <div className={infoBox}>
      <div className={innerContentHeading}>
        <Text as="h3" variant="body" className={innerContentHeadingText}>
          Promo Code
        </Text>
        {hasMembership && (
          <div className={innerContentEditWrapper}>
            <NextLink href="/shop/pages/referrals" passHref>
              <Text as={'a'} variant={'body'} className={innerContentEditText} target="_blank">
                Give $30, Get $30
              </Text>
            </NextLink>
          </div>
        )}
      </div>
      <FormCoupon
        placeholder="Add Discount Code"
        appliedCoupons={discountCodesSub}
        handleAddCoupon={handleAddCoupon}
        setAppliedCoupons={setDiscountCodesSub}
      />
      <div className={infoBoxAppliedCouponsWrapper}>
        {discountCodesSub?.length > 0 && (
          <Text as="p" variant="body" className={infoBoxAppliedCouponsInnerText}>
            Currently applied to next order:
          </Text>
        )}
        <div className={infoBoxAppliedCouponsInnerWrapper}>
          {discountCodesSub?.map((code, index) => (
            <AppliedCodeBox key={index} code={code} handleRemoveCoupon={handleRemoveCoupon} />
          ))}
        </div>
        {errorRemove && (
          <Text as="p" variant="body" className={infoBoxAppliedCouponsInnerText}>
            {errorRemove}
          </Text>
        )}
      </div>
    </div>
  );
};

export default CouponBox;
